// @ts-nocheck
/**
 * Publishing Scheduler Component
 * Interface for scheduling content publication with timezone support
 * Integrates with ContentEditor and ApprovalWorkflowDashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Form,
  FormGroup,
  DatePicker,
  DatePickerInput,
  TimePicker,
  TimePickerSelect,
  SelectItem,
  Dropdown,
  Button,
  Toggle,
  Tag,
  InlineNotification,
  Modal,
  Tile,
  Stack,
  Heading,
} from '@carbon/react';
import { Calendar, Time, EventSchedule, Warning, Checkmark, Reset } from '@carbon/icons-react';

interface PublishingSchedulerProps {
  contentId?: string;
  initialSchedule?: ScheduleData;
  onScheduleSet: (schedule: ScheduleData) => void;
  onScheduleCancel: () => void;
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

interface ScheduleData {
  publishDate: string;
  publishTime: string;
  timezone: string;
  isImmediate: boolean;
  scheduleId?: string;
}

interface TimezoneOption {
  id: string;
  text: string;
  offset: string;
}

const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { id: 'America/New_York', text: 'Eastern Time (ET)', offset: 'UTC-5/-4' },
  { id: 'America/Chicago', text: 'Central Time (CT)', offset: 'UTC-6/-5' },
  { id: 'America/Denver', text: 'Mountain Time (MT)', offset: 'UTC-7/-6' },
  { id: 'America/Los_Angeles', text: 'Pacific Time (PT)', offset: 'UTC-8/-7' },
  { id: 'Europe/London', text: 'Greenwich Mean Time (GMT)', offset: 'UTC+0/+1' },
  { id: 'Europe/Berlin', text: 'Central European Time (CET)', offset: 'UTC+1/+2' },
  { id: 'Asia/Tokyo', text: 'Japan Standard Time (JST)', offset: 'UTC+9' },
  { id: 'Australia/Sydney', text: 'Australian Eastern Time (AET)', offset: 'UTC+10/+11' },
  { id: 'America/Sao_Paulo', text: 'Brasília Time (BRT)', offset: 'UTC-3' },
  { id: 'UTC', text: 'Coordinated Universal Time (UTC)', offset: 'UTC+0' },
];

const PublishingScheduler: React.FC<PublishingSchedulerProps> = ({
  contentId,
  initialSchedule,
  onScheduleSet,
  onScheduleCancel,
  isOpen,
  onClose,
  readOnly = false,
}) => {
  const [schedule, setSchedule] = useState<ScheduleData>({
    publishDate: '',
    publishTime: '',
    timezone: 'America/New_York',
    isImmediate: true,
    ...initialSchedule,
  });

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (initialSchedule) {
      setSchedule({ ...schedule, ...initialSchedule });
    }
  }, [initialSchedule]);

  const validateSchedule = (): boolean => {
    const errors: string[] = [];

    if (!schedule.isImmediate) {
      if (!schedule.publishDate) {
        errors.push('Publish date is required for scheduled content');
      }

      if (!schedule.publishTime) {
        errors.push('Publish time is required for scheduled content');
      }

      if (schedule.publishDate && schedule.publishTime) {
        const scheduledDateTime = new Date(`${schedule.publishDate}T${schedule.publishTime}`);
        const now = new Date();

        if (scheduledDateTime <= now) {
          errors.push('Scheduled publish time must be in the future');
        }

        // Check if scheduled time is more than 1 year in the future
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

        if (scheduledDateTime > oneYearFromNow) {
          errors.push('Scheduled publish time cannot be more than 1 year in the future');
        }
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleScheduleSubmit = async () => {
    if (!validateSchedule()) {
      return;
    }

    setIsLoading(true);

    try {
      const scheduleData: ScheduleData = {
        ...schedule,
        scheduleId: schedule.scheduleId || `schedule_${Date.now()}`,
      };

      onScheduleSet(scheduleData);
      onClose();
    } catch (error) {
      console.error('Failed to set schedule:', error);
      setValidationErrors(['Failed to set schedule. Please try again.']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScheduleCancel = () => {
    onScheduleCancel();
    onClose();
  };

  const formatScheduledDateTime = (): string | null => {
    if (schedule.isImmediate || !schedule.publishDate || !schedule.publishTime) {
      return null;
    }

    const selectedTimezone = TIMEZONE_OPTIONS.find(tz => tz.id === schedule.timezone);
    const dateTime = new Date(`${schedule.publishDate}T${schedule.publishTime}`);

    return `${dateTime.toLocaleDateString()} at ${dateTime.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })} (${selectedTimezone?.text || schedule.timezone})`;
  };

  const getMinDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = (): string => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    return oneYearFromNow.toISOString().split('T')[0];
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading="Schedule Content Publication"
      modalLabel="Content Scheduling"
      primaryButtonText={schedule.isImmediate ? 'Publish Now' : 'Schedule Publication'}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleScheduleSubmit}
      onSecondarySubmit={handleScheduleCancel}
      preventCloseOnClickOutside={true}
      size="md"
      loading={isLoading}
    >
      <div style={{ marginBottom: '1rem' }}>
        <Text>
          Choose when to publish this content. You can publish immediately or schedule for a future date and time.
        </Text>
      </div>

      {validationErrors.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {validationErrors.map((error, index) => (
            <InlineNotification
              key={index}
              kind="error"
              title="Validation Error"
              subtitle={error}
              lowContrast
              hideCloseButton
            />
          ))}
        </div>
      )}

      <Form>
        <FormGroup legendText="Publication Timing">
          <Toggle
            id="immediate-toggle"
            labelText="Publish immediately"
            toggled={schedule.isImmediate}
            onToggle={toggled => setSchedule({ ...schedule, isImmediate: toggled })}
            disabled={readOnly}
          />
        </FormGroup>

        {!schedule.isImmediate && (
          <>
            <Grid style={{ marginTop: '1rem' }}>
              <Column sm={2} md={2} lg={2}>
                <FormGroup>
                  <DatePicker
                    datePickerType="single"
                    value={schedule.publishDate}
                    onChange={dates => {
                      const date = dates[0];
                      setSchedule({
                        ...schedule,
                        publishDate: date ? date.toISOString().split('T')[0] : '',
                      });
                    }}
                    minDate={getMinDate()}
                    maxDate={getMaxDate()}
                    disabled={readOnly}
                  >
                    <DatePickerInput
                      placeholder="mm/dd/yyyy"
                      labelText="Publish Date"
                      id="publish-date"
                      invalid={validationErrors.some(e => e.includes('date'))}
                    />
                  </DatePicker>
                </FormGroup>
              </Column>

              <Column sm={2} md={2} lg={2}>
                <FormGroup>
                  <TimePicker
                    id="publish-time"
                    labelText="Publish Time"
                    value={schedule.publishTime}
                    onChange={event => {
                      setSchedule({ ...schedule, publishTime: event.target.value });
                    }}
                    invalid={validationErrors.some(e => e.includes('time'))}
                    disabled={readOnly}
                  />
                </FormGroup>
              </Column>
            </Grid>

            <FormGroup style={{ marginTop: '1rem' }}>
              <Dropdown
                id="timezone-select"
                titleText="Timezone"
                label="Select timezone"
                items={TIMEZONE_OPTIONS}
                selectedItem={TIMEZONE_OPTIONS.find(tz => tz.id === schedule.timezone)}
                itemToString={item => (item ? `${item.text} (${item.offset})` : '')}
                onChange={({ selectedItem }) => {
                  if (selectedItem) {
                    setSchedule({ ...schedule, timezone: selectedItem.id });
                  }
                }}
                disabled={readOnly}
              />
            </FormGroup>

            {formatScheduledDateTime() && (
              <Tile style={{ marginTop: '1rem', backgroundColor: '#f4f4f4' }}>
                <Stack gap={2}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Schedule size={20} />
                    <Heading size="sm">Scheduled Publication</Heading>
                  </div>
                  <Text size="lg" style={{ fontWeight: 500 }}>
                    {formatScheduledDateTime()}
                  </Text>
                  <Text size="sm" style={{ color: '#525252' }}>
                    Content will be automatically published at the scheduled time.
                  </Text>
                </Stack>
              </Tile>
            )}
          </>
        )}

        {schedule.isImmediate && (
          <Tile style={{ marginTop: '1rem', backgroundColor: '#e8f5e8' }}>
            <Stack gap={2}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Checkmark size={20} style={{ color: '#24a148' }} />
                <Heading size="sm">Immediate Publication</Heading>
              </div>
              <Text>Content will be published immediately after approval workflow completion.</Text>
            </Stack>
          </Tile>
        )}

        {initialSchedule?.scheduleId && (
          <div style={{ marginTop: '1rem' }}>
            <InlineNotification
              kind="info"
              title="Existing Schedule"
              subtitle={`This content is currently scheduled. Schedule ID: ${initialSchedule.scheduleId}`}
              lowContrast
              hideCloseButton
            />
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default PublishingScheduler;
